import {
	CONNECTORS_FORM_KEYS,
	ConnectorKeys,
	ConnectorTypeEnum,
	IErrorMessage
} from 'types/connectors';
import React, { useEffect, useRef, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import TextFieldValidation from '@components/TextFieldValidation/TextFieldValidation';
import { LoadingButton } from '@mui/lab';
import { string, object } from 'yup';
import { useFormik } from 'formik';
import {
	emailValidationScheme,
	requiredPhoneValidation
} from '@utils/validation';

interface IConnectorTestProps {
	type: ConnectorKeys;
	gridSpacing: number;
	onSubmit: (
		target: string,
		onComplete: () => void,
		onFail: (props: IErrorMessage) => void
	) => Promise<unknown>;
	disabled?: boolean;
}

const getTypeValidation = (providerType: ConnectorKeys) => {
	switch (providerType) {
		case ConnectorTypeEnum.EMAIL_PROVIDERS:
			return emailValidationScheme().notRequired();
		case ConnectorTypeEnum.SMS_PROVIDERS:
			return requiredPhoneValidation();
		default:
			return string().notRequired();
	}
};

enum StatusCode {
	SUCCESS = 'success',
	ERROR = 'error'
}

type IStatusCode = StatusCode.SUCCESS | StatusCode.ERROR;

interface IStatus {
	message: string;
	type: IStatusCode;
}

const getStatusColor = (type: IStatusCode) => {
	switch (type) {
		case StatusCode.SUCCESS:
			return 'success.main';
		case StatusCode.ERROR:
			return 'error';
		default:
			return 'primary';
	}
};

const ConnectorTest = ({
	type,
	gridSpacing,
	disabled,
	onSubmit
}: IConnectorTestProps) => {
	const [status, setStatus] = useState<IStatus | null>(null);
	const [processingTest, setProcessingTest] = useState<boolean>(false);
	const ref = useRef<HTMLDivElement | null>(null);

	const onTestFail = (error: IErrorMessage) => {
		setStatus({
			message:
				error?.message ||
				intl.tr(`connectors.test.message.${type}.genericError`),
			type: StatusCode.ERROR
		});
		setProcessingTest(false);
	};

	const onTestComplete = () => {
		setStatus({
			message: intl.tr(`connectors.test.message.${type}.success`),
			type: StatusCode.SUCCESS
		});
		setProcessingTest(false);
	};

	const onTestSubmit = async ({ target }: { target: string }) => {
		setStatus(null);
		setProcessingTest(true);
		await onSubmit(target, onTestComplete, onTestFail);
	};

	const formik = useFormik<any>({
		initialValues: {
			target: ''
		},
		enableReinitialize: true,
		validationSchema: object({ target: getTypeValidation(type) }),
		onSubmit: onTestSubmit
	});

	useEffect(() => {
		if (status && ref.current) {
			ref.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
		}
	}, [status]);

	return (
		<Grid container spacing={gridSpacing} alignItems="center" ref={ref}>
			<Grid item xs={10}>
				<TextFieldValidation
					id={CONNECTORS_FORM_KEYS.target}
					type="text"
					formik={formik}
					label={intl.tr(`connectors.test.${type}.placeholder`)}
					fullWidth
					disabled={disabled || processingTest}
					inputProps={{ 'data-testid': `test-target` }}
					onFocus={() => setStatus(null)}
				/>
			</Grid>
			<Grid item xs={2}>
				<LoadingButton
					loading={processingTest}
					onClick={() => formik.handleSubmit()}
					color="primary"
					variant="outlined"
					disabled={
						!formik.dirty || !formik.isValid || disabled || processingTest
					}
					data-testid="test-target-submit"
				>
					{intl.tr(`button.send`)}
				</LoadingButton>
			</Grid>
			{status?.message && (
				<Grid item data-testid="test-status">
					<Typography variant="body2" color={getStatusColor(status.type)}>
						{status.message}
					</Typography>
				</Grid>
			)}
		</Grid>
	);
};

ConnectorTest.defaultProps = {
	disabled: false
};

export default ConnectorTest;
